import React, { useEffect, useMemo, useState } from "react";
import api, { fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { amountInWords } from "../lib/amountWords";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Landmark, Plus, Trash2, Send, FileDown, Save, MessageCircle, CheckCircle2, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";

export const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
// Ready-made list (as in the temple's register); staff only type the amounts. Other countries can be added.
export const FOREIGN_LIST = [
  ["U.S.A", "Dollars"], ["Malaysia", "Ringgit"], ["United Arab Emirates", "Dirhams"], ["Bhutan", "Ngultrum"], ["Europe", "Euro"],
  ["England", "Pounds"], ["Tanzania", "Shilling"], ["Vietnam", "Dong"], ["Qatar", "Riyal"], ["Oman", "Baisa"], ["Canada", "Dollars"],
  ["Australia", "Dollar"], ["Singapore", "Dollar"], ["New Zealand", "Dollar"], ["Japan", "Yen"], ["Maldives", "Rufiyaa"],
  ["Kuwait", "Dinar"], ["Nigeria", "Naira"],
];
const OLD_DENOMS = [2000, 1000, 500];
const EMPTY = {
  bank_name: "", branch_name: "", manager_name: "", last_opened_date: "", opened_date: today(),
  gold: { kg: "", g: "", mg: "" }, silver: { kg: "", g: "", mg: "" }, cash_notes: "", cash_coins: "",
  foreign: FOREIGN_LIST.map(([country, currency]) => ({ country, currency, amount: "", preset: true })),
  old_notes: OLD_DENOMS.map((d) => ({ denomination: String(d), count: "", preset: true })), remarks: "",
};
const n = (v) => Number(v) || 0;

export async function downloadHundialPdf(r) {
  try {
    const res = await api.get(`/hundial/reports/${r.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `Hundial_Report_${r.report_no}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  } catch (e) { toast.error(fmtErr(e)); }
}

export function WaStatus({ items }) {
  if (!items?.length) return <span className="text-xs text-slate-400">Not sent</span>;
  const bad = (s) => /FAILED|NOT SENT/.test(s);
  const icon = (s) => (bad(s) ? <XCircle className="w-3.5 h-3.5 text-red-600" /> : /QUEUED|MOCK/.test(s) ? <Clock className="w-3.5 h-3.5 text-amber-600" /> : <CheckCircle2 className="w-3.5 h-3.5 text-green-700" />);
  return (
    <ul className="space-y-0.5" data-testid="hundial-wa-status">
      {items.map((w, i) => (
        <li key={i} className="flex flex-wrap items-center gap-1.5 text-xs" title={w.error ? String(w.error) : ""}>
          {icon(w.status)}<span className="font-mono">{w.mobile}</span>{w.name && <span className="text-slate-500">({w.name})</span>}
          <span className={bad(w.status) ? "text-red-700" : "text-slate-600"}>{/MOCK/.test(w.status) ? "Test mode - not sent (WhatsApp not set up)" : w.status}</span>
          {w.error && <span className="text-[11px] text-red-700 block w-full pl-5">{String(w.error)}</span>}
        </li>
      ))}
    </ul>
  );
}

function Metal({ label, value, onChange, testid }) {
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-wider text-gold-700 mb-1">{label}</p>
      <div className="grid grid-cols-3 gap-2">
        {[["kg", "Kgs"], ["g", "Gms"], ["mg", "Mgrs"]].map(([k, l]) => (
          <div key={k}><Label className="text-xs">{label} ({l})</Label>
            <Input type="number" min={0} step="any" value={value[k]} onChange={(e) => onChange({ ...value, [k]: e.target.value })} data-testid={`${testid}-${k}`} /></div>
        ))}
      </div>
      <p className="text-[11px] text-slate-500 mt-1">Total: {(n(value.kg) * 1000 + n(value.g) + n(value.mg) / 1000).toLocaleString("en-IN", { maximumFractionDigits: 3 })} grams</p>
    </div>
  );
}

const Auto = ({ label, value, testid }) => (
  <div>
    <Label className="text-xs">{label} <span className="text-[10px] text-green-700 font-semibold">(auto)</span></Label>
    <div className="h-10 rounded-md border border-gold-300 bg-gold-50 px-3 flex items-center font-semibold text-maroon-900" data-testid={testid}>{value}</div>
  </div>
);

function EntryForm({ templeId }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  // Auto calculations (the server recalculates the same on save)
  const calc = useMemo(() => {
    const days = f.last_opened_date && f.opened_date ? Math.round((new Date(f.opened_date) - new Date(f.last_opened_date)) / 86400000) : 0;
    const total = n(f.cash_notes) + n(f.cash_coins);
    const foreignTotals = {};
    f.foreign.forEach((r) => { const c = r.currency.trim(); if (c && n(r.amount)) foreignTotals[c] = (foreignTotals[c] || 0) + n(r.amount); });
    const foreignFilled = f.foreign.filter((r) => n(r.amount) > 0).length;
    const oldTotal = f.old_notes.reduce((s, r) => s + n(r.denomination) * n(r.count), 0);
    return { days, total, avg: days > 0 ? total / days : 0, foreignTotals, foreignFilled, oldTotal };
  }, [f]);

  const rowSet = (list, i, k, v) => set(list, f[list].map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const save = async (send) => {
    if (calc.days <= 0) { toast.error("Present opening date must be after the last opened date"); return; }
    setBusy(true);
    try {
      const r = await api.post("/hundial/reports", {
        ...f, temple_id: templeId, send,
        foreign: f.foreign.filter((x) => n(x.amount) > 0), old_notes: f.old_notes.filter((x) => n(x.count) > 0),
      });
      setSaved(r.data);
      toast.success(`Report ${r.data.report_no} saved${send && r.data.whatsapp?.length ? ` and sent to ${r.data.whatsapp.length} WhatsApp numbers` : ""}`);
      setF({ ...EMPTY, opened_date: today() });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      {saved && (
        <Card className="border-2 border-green-300 bg-green-50" data-testid="hundial-saved">
          <CardContent className="p-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-heading font-bold text-green-900">Report {saved.report_no} saved · Total {inr(saved.cash_total)}</p>
              <p className="text-xs text-slate-600 mb-1">PDF generated and stored securely. WhatsApp delivery:</p>
              <WaStatus items={saved.whatsapp} />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => downloadHundialPdf(saved)} data-testid="hundial-saved-pdf"><FileDown className="w-4 h-4 mr-1" /> PDF</Button>
              <Button variant="outline" onClick={() => setSaved(null)}>New entry</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="bg-[#EFF6FF] border-blue-300"><CardHeader className="pb-2"><CardTitle className="font-heading text-lg text-blue-900">Bank Details</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-3 gap-3">
          <div><Label className="text-xs">Bank Name *</Label><Input value={f.bank_name} onChange={(e) => set("bank_name", e.target.value)} data-testid="hundial-bank" /></div>
          <div><Label className="text-xs">Branch Name</Label><Input value={f.branch_name} onChange={(e) => set("branch_name", e.target.value)} data-testid="hundial-branch" /></div>
          <div><Label className="text-xs">Manager Name</Label><Input value={f.manager_name} onChange={(e) => set("manager_name", e.target.value)} data-testid="hundial-manager" /></div>
        </CardContent></Card>

      <Card><CardHeader className="pb-2"><CardTitle className="font-heading text-lg text-maroon-900">Hundial Opening Details</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-3 gap-3">
          <div><Label className="text-xs">Last Hundial Opened Date *</Label><Input type="date" max={f.opened_date} value={f.last_opened_date} onChange={(e) => set("last_opened_date", e.target.value)} data-testid="hundial-last-date" /></div>
          <div><Label className="text-xs">Present Hundial Opening Date *</Label><Input type="date" max={today()} value={f.opened_date} onChange={(e) => set("opened_date", e.target.value)} data-testid="hundial-date" /></div>
          <Auto label="Total Period (Days)" value={calc.days > 0 ? `${calc.days} days` : "-"} testid="hundial-days" />
        </CardContent></Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="bg-[#FFF8DC] border-2 border-[#D4A017]"><CardHeader className="pb-2"><CardTitle className="font-heading text-lg text-amber-900">Gold Details</CardTitle></CardHeader>
          <CardContent><Metal label="Gold" value={f.gold} onChange={(v) => set("gold", v)} testid="hundial-gold" /></CardContent></Card>
        <Card className="bg-[#F3F4F6] border-slate-300"><CardHeader className="pb-2"><CardTitle className="font-heading text-lg text-slate-700">Silver Details</CardTitle></CardHeader>
          <CardContent><Metal label="Silver" value={f.silver} onChange={(v) => set("silver", v)} testid="hundial-silver" /></CardContent></Card>
      </div>

      <Card className="bg-[#ECFDF5] border-2 border-emerald-400"><CardHeader className="pb-2"><CardTitle className="font-heading text-lg text-emerald-900">Cash Collection</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div><Label className="text-xs">Indian Currency Notes (₹)</Label><Input type="number" min={0} step="any" value={f.cash_notes} onChange={(e) => set("cash_notes", e.target.value)} data-testid="hundial-notes" /></div>
          <div><Label className="text-xs">Indian Currency Coins (₹)</Label><Input type="number" min={0} step="any" value={f.cash_coins} onChange={(e) => set("cash_coins", e.target.value)} data-testid="hundial-coins" /></div>
          <Auto label="Total Rupees" value={inr(calc.total)} testid="hundial-total" />
          <Auto label="Average One Day Collection" value={calc.days > 0 ? inr(calc.avg) : "-"} testid="hundial-avg" />
          {calc.total > 0 && <p className="sm:col-span-2 lg:col-span-4 text-xs italic text-slate-600">({amountInWords(calc.total)})</p>}
        </CardContent></Card>

      <Card className="bg-[#F5F3FF] border-2 border-purple-400"><CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <div><CardTitle className="font-heading text-lg text-purple-900">Foreign Currency</CardTitle>
          <p className="text-xs text-slate-500">Type the quantity against each country; leave blank if none. {calc.foreignFilled} entered.</p></div>
        <Button size="sm" variant="outline" className="bg-white" onClick={() => set("foreign", [...f.foreign, { country: "", currency: "", amount: "" }])} data-testid="hundial-foreign-add"><Plus className="w-4 h-4 mr-1" /> Add other country</Button></CardHeader>
        <CardContent>
          <table className="w-full text-sm bg-white rounded-md overflow-hidden" data-testid="hundial-foreign-table">
            <thead><tr className="bg-purple-600 text-white text-left text-xs"><th className="px-2 py-2 w-12 text-center">S.No</th><th className="px-2 py-2">Country (Currency)</th><th className="px-2 py-2 w-44">Quantity</th><th className="w-10" /></tr></thead>
            <tbody>
              {f.foreign.map((r, i) => (
                <tr key={i} className={`border-b border-purple-100 ${n(r.amount) > 0 ? "bg-purple-50" : ""}`}>
                  <td className="px-2 py-1 text-center text-slate-500">{i + 1}</td>
                  <td className="px-2 py-1">
                    {r.preset ? <span className="font-medium">{r.country} <span className="text-slate-500">({r.currency})</span></span> : (
                      <div className="grid grid-cols-2 gap-2">
                        <Input className="h-8" value={r.country} onChange={(e) => rowSet("foreign", i, "country", e.target.value)} placeholder="Country, e.g. Sri Lanka" />
                        <Input className="h-8" value={r.currency} onChange={(e) => rowSet("foreign", i, "currency", e.target.value)} placeholder="Currency, e.g. Rupee" />
                      </div>)}
                  </td>
                  <td className="px-2 py-1"><Input className="h-8 text-right" type="number" min={0} step="any" value={r.amount} onChange={(e) => rowSet("foreign", i, "amount", e.target.value)} data-testid={`hundial-foreign-amt-${i}`} /></td>
                  <td className="px-1">{!r.preset && <Button variant="ghost" size="icon" className="h-8 w-8" title="Remove" onClick={() => set("foreign", f.foreign.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-red-600" /></Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent></Card>

      <Card className="bg-[#FEF3C7] border-2 border-amber-400"><CardHeader className="pb-2 flex flex-row items-center justify-between"><CardTitle className="font-heading text-lg text-amber-900">Old Notes</CardTitle>
        <Button size="sm" variant="outline" className="bg-white" onClick={() => set("old_notes", [...f.old_notes, { denomination: "", count: "" }])} data-testid="hundial-old-add"><Plus className="w-4 h-4 mr-1" /> Add row</Button></CardHeader>
        <CardContent className="space-y-2">
          {f.old_notes.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end">
              <div><Label className="text-xs">Denomination (₹)</Label><Input type="number" min={0} value={r.denomination} onChange={(e) => rowSet("old_notes", i, "denomination", e.target.value)} placeholder="e.g. 1000" /></div>
              <div><Label className="text-xs">Number of Notes</Label><Input type="number" min={0} value={r.count} onChange={(e) => rowSet("old_notes", i, "count", e.target.value)} /></div>
              <Auto label="Total Value" value={inr(n(r.denomination) * n(r.count))} />
              {r.preset ? <span className="w-10" /> : <Button variant="ghost" size="icon" title="Remove" onClick={() => set("old_notes", f.old_notes.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-red-600" /></Button>}
            </div>
          ))}
          {calc.oldTotal > 0 && <p className="text-sm font-semibold text-maroon-900" data-testid="hundial-old-total"><span className="text-[10px] text-green-700">(auto)</span> Old notes total: {inr(calc.oldTotal)}</p>}
        </CardContent></Card>

      <Card><CardContent className="p-4"><Label className="text-xs">Remarks</Label>
        <Textarea rows={2} value={f.remarks} onChange={(e) => set("remarks", e.target.value)} placeholder="Optional" /></CardContent></Card>

      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outline" disabled={busy} onClick={() => save(false)} data-testid="hundial-save"><Save className="w-4 h-4 mr-1" /> Save & generate PDF</Button>
        <Button disabled={busy} onClick={() => save(true)} className="bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="hundial-save-send"><Send className="w-4 h-4 mr-1" /> Save, generate PDF & send WhatsApp</Button>
      </div>
    </div>
  );
}

function Recipients({ templeId }) {
  const [rows, setRows] = useState([]);
  const [max, setMax] = useState(20);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!templeId) return;
    api.get("/hundial/recipients", { params: { temple_id: templeId } }).then((r) => {
      setMax(r.data.max);
      const list = r.data.recipients.map((x) => ({ ...x }));
      setRows(list.length ? list : [{ name: "", mobile: "" }]);
    }).catch((e) => toast.error(fmtErr(e)));
  }, [templeId]);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/hundial/recipients", { temple_id: templeId, recipients: rows });
      setRows(r.data.recipients.length ? r.data.recipients : [{ name: "", mobile: "" }]);
      toast.success(`${r.data.recipients.length} WhatsApp numbers saved`);
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  return (
    <Card data-testid="hundial-recipients">
      <CardHeader className="pb-2"><CardTitle className="font-heading text-lg text-maroon-900 flex items-center gap-2"><MessageCircle className="w-5 h-5" /> WhatsApp Recipients ({rows.filter((r) => r.mobile).length}/{max})</CardTitle>
        <p className="text-xs text-slate-500">The Hundial Opening Report PDF and official notices are sent to these numbers through the temple's WhatsApp Business account.</p></CardHeader>
      <CardContent className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[2.5rem_1fr_1fr_auto] gap-2 items-center">
            <span className="text-xs font-semibold text-slate-500">#{i + 1}</span>
            <Input placeholder={`Mobile Number ${i + 1}`} inputMode="numeric" maxLength={10} value={r.mobile} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, mobile: e.target.value.replace(/\D/g, "") } : x)))} data-testid={`hundial-recipient-${i}`} />
            <Input placeholder="Name / designation (optional)" value={r.name} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
            <Button variant="ghost" size="icon" title="Remove" onClick={() => setRows(rows.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-red-600" /></Button>
          </div>
        ))}
        <div className="flex justify-between pt-1">
          <Button variant="outline" size="sm" disabled={rows.length >= max} onClick={() => setRows([...rows, { name: "", mobile: "" }])} data-testid="hundial-recipient-add"><Plus className="w-4 h-4 mr-1" /> Add number</Button>
          <Button onClick={save} disabled={busy} className="bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="hundial-recipients-save">Save numbers</Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function HundialEntry() {
  const { user } = useAuth();
  const [temples, setTemples] = useState([]);
  const [templeId, setTempleId] = useState(user.temple_id || "");
  useEffect(() => {
    api.get("/meta/form-data").then((r) => {
      setTemples(r.data.temples || []);
      if (!templeId && r.data.temples?.length) setTempleId(r.data.temples[0].temple_id);
    }).catch(() => {});
  }, []); // eslint-disable-line
  const temple = temples.find((t) => t.temple_id === templeId);
  return (
    <div className="max-w-5xl mx-auto" data-testid="hundial-entry-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-gold-700 font-semibold">Hundial Opening Report Management</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-maroon-900 flex items-center gap-2"><Landmark className="w-7 h-7 text-gold-600" /> Hundial Opening</h1>
          {temple && <p className="text-sm text-slate-600">{temple.name}</p>}
        </div>
        {user.role === "SUPER_ADMIN" && (
          <select value={templeId} onChange={(e) => setTempleId(e.target.value)} className="h-10 rounded-lg border border-[#E5D9C5] px-3 text-sm bg-white" data-testid="hundial-temple">
            {temples.map((t) => <option key={t.temple_id} value={t.temple_id}>{t.temple_id} · {t.name}</option>)}
          </select>
        )}
      </div>
      <Tabs defaultValue="entry">
        <TabsList className="bg-white border border-[#E5D9C5]">
          <TabsTrigger value="entry" data-testid="hundial-tab-entry">New Report</TabsTrigger>
          <TabsTrigger value="recipients" data-testid="hundial-tab-recipients">WhatsApp Recipients</TabsTrigger>
        </TabsList>
        <TabsContent value="entry" className="mt-4">{templeId && <EntryForm key={templeId} templeId={templeId} />}</TabsContent>
        <TabsContent value="recipients" className="mt-4">{templeId && <Recipients templeId={templeId} />}</TabsContent>
      </Tabs>
    </div>
  );
}
